import {
  Button,
  Card,
  CardActions,
  CardContent,
  Grid,
  Skeleton,
  SxProps,
  ToggleButton,
  Typography,
} from '@mui/material';
import { grey } from '@mui/material/colors';
import React, { useEffect } from 'react';
import { useUiState } from '@utils/UiStateHook';
import DisabledCardState from '@components//Cards/DisabledCardState';
import { ToDate } from '@utils/DateUtils';
import { DateTime } from 'luxon';
import { GoalStatus } from '@app/enums/GoalStatus';
import { Circle, CircleOutlined } from '@mui/icons-material';
import { ConfirmGoalCompletionDialog, GoalWithStatus, UpdateGoalsDialog } from '@components';
import { useIsFirstWeek } from '@app/hooks/useIsFirstWeek';
import { ExerciseHistory } from '@components/modals';
import { useJailCardModalController } from '@components/GoalTracking/useJailCardModalController';
import { useConfirmGoalCompletionDialogController } from '@components/GoalTracking/useConfirmGoalCompletionDialogController';
import { JailCardStatusDialog } from '@components/GoalTracking/jail-card-status-dialog';
import { useUpdateGoalsDialogController } from '@components/GoalTracking/useUpdateGoalsDialogController';
import { useExerciseHistoryController } from '@components/GoalTracking/useExerciseHistoryController';
import { GoalSummaryView } from '@views';
import { useSurveyDrawerControllerContext } from '@components/SurveyDrawer/SurveyDrawerControllerProvider';
import { useGoalTrackerCardController } from '@components/GoalTracking/useGoalTrackerCardController';
import { InfoBubble } from '../InfoBubble';

interface GoalTrackerCardProps {
  groupStartDate: string | DateTime;
  cohortUserId: number;
  groupId: number;
  weekNumberIndexZero: number;
  reloadDashboardView?: () => void; // Optional prop to reload the dashboard view
  // Reports up whether everything that decides the Goals modal's visibility has loaded. The
  // dashboard uses this to hold its loader until the modal's outcome is known, so it can't
  // flash open before the goal-card/activity-lookup queries resolve.
  onReadyChange?: (ready: boolean) => void;
  // Reports up whether the Goals modal is currently open, so the dashboard can avoid stacking
  // lower-priority overlays (the info panel) on top of it.
  onOpenChange?: (open: boolean) => void;
}

export type GoalCompletionStatus = {
  [goalId: number]: GoalStatus[];
};

export const GoalTrackerCard2: React.FC<GoalTrackerCardProps> = ({
  groupStartDate,
  cohortUserId,
  groupId,
  weekNumberIndexZero,
  reloadDashboardView,
  onReadyChange,
  onOpenChange,
}) => {
  const { view, isLoading, refetch, enabled, disabledMessage } = useGoalTrackerCardController(
    cohortUserId,
    groupStartDate
  );
  const { refresh: reloadSurveyDrawer } = useSurveyDrawerControllerContext();
  const { isFirstWeek } = useIsFirstWeek(ToDate(groupStartDate));

  const uiState = useUiState(enabled);
  const styles = {
    card: {
      display: 'flex',
      flexDirection: 'column',
      border: '1px solid',
      borderColor: uiState.borderColor,
      borderRadius: 4,
    } as SxProps,
  };

  const jailCardController = useJailCardModalController(view);
  const confirmGoalDialogController = useConfirmGoalCompletionDialogController(cohortUserId, refetch);
  const updateGoalsDialogController = useUpdateGoalsDialogController(groupId, refetch, ToDate(groupStartDate));
  const exerciseHistoryController = useExerciseHistoryController(cohortUserId);
  const { isDrawerOpen } = useSurveyDrawerControllerContext();

  // Report up whether the Goals modal's outcome is known yet: the goal card must have
  // loaded (so we know hasGoalsForCurrentPeriod) and the dialog controller's own data
  // (activity lookup + cohort-user role) must be ready. The dashboard holds its loader
  // until this is true so the modal never flashes before we know whether to show it.
  const goalsReady = !isLoading && !!view && updateGoalsDialogController.isReady;
  useEffect(() => {
    onReadyChange?.(goalsReady);
  }, [goalsReady, onReadyChange]);

  // Whether the Goals modal is actually on screen. Require the goal card to have loaded
  // before deciding — `view` is undefined while useGoalCardViewCreator is in flight, and the
  // old `!view?.hasGoalsForCurrentPeriod` treated that as "no goals" and flashed the dialog
  // open, then closed it once the data revealed the user already had goals.
  const isGoalModalOpen =
    !isLoading &&
    !!view &&
    updateGoalsDialogController.showNewGoalDialog &&
    !isDrawerOpen &&
    !view.hasGoalsForCurrentPeriod;

  // Report the open state up so the dashboard can keep lower-priority overlays (the info
  // panel) from stacking on top of the Goals modal, which would steal its Escape/focus.
  useEffect(() => {
    onOpenChange?.(isGoalModalOpen);
  }, [isGoalModalOpen, onOpenChange]);

  const [newGoalEnabled, setNewGoalEnabled] = React.useState<boolean>(false);
  const [goals, setGoals] = React.useState<{
    goals: GoalSummaryView[];
    progress: GoalCompletionStatus;
  }>({
    goals: [],
    progress: {},
  });

  useEffect(() => {
    if (!isLoading && view) {
      setGoals(view.goalsWithProgress);
    }
  }, [isLoading, view]);

  useEffect(() => {
    // If this is not week 1
    // And the user has no goals for the current period
    // And the cohort is still within the exercise period (week 12 or earlier)
    // Then enable the new goal button
    if (view) {
      setNewGoalEnabled(!isFirstWeek && weekNumberIndexZero <= 12 && !view.hasGoalsForCurrentPeriod);
    }
  }, [isFirstWeek, view, weekNumberIndexZero]);

  return (
    <>
      <Card variant={'elevation'} sx={styles.card}>
        <CardContent>
          <div
            className="title-with-info"
            style={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              gap: 6, // space between title and info icon
              flexWrap: 'nowrap',
            }}
          >
            <Typography variant="h5" component="div" sx={{ color: uiState.color }}>
              My Exercise Goal
            </Typography>
            <InfoBubble title="My Exercise Goal">
              Each time you complete an exercise activity, click one of the bubbles to record progress toward your goal.
              If you need to adjust your goal due to injury or illness, DM Emily in the chat spot. You have a chance to
              update your goal at the end of week 4 and week 8. If you want to tweak your goal or have any questions,
              just DM your coach.
            </InfoBubble>
          </div>
          {!enabled && (
            <DisabledCardState disabledMessage={disabledMessage} src="/assets/svg/calendar.svg" alt="Get stoked!" />
          )}
          {isLoading && (
            <ul>
              <li>
                <Typography variant={'h6'} sx={{ fontWeight: 300, color: uiState.color }}>
                  <Skeleton width={'80%'}></Skeleton>
                </Typography>
              </li>
            </ul>
          )}
          {!isLoading && (
            <Grid
              container
              sx={{
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {isFirstWeek && enabled && (
                <>
                  {goals.goals && goals.goals.length > 0 && (
                    <>
                      <p>My upcoming goal:</p>
                      {goals.goals?.map((x) => (
                        <GoalGrid
                          key={x.id}
                          goal={{ goal: x, progress: goals.progress[x.id] }}
                          goalCompletionStatus={goals.progress}
                          handleToggleActivityStatus={() => {}} // No action needed for first week
                          enabled={false} // Disable interaction for first week
                        ></GoalGrid>
                      ))}
                    </>
                  )}
                  <Typography
                    sx={{
                      marginTop: '20px',
                      textAlign: 'center',
                      fontSize: '16px',
                      fontWeight: '400',
                      color: enabled ? undefined : grey[500],
                    }}
                    variant="h5"
                    component="p"
                    gutterBottom
                  >
                    Don&apos;t worry! We won&apos;t start tracking goals until next week.
                  </Typography>
                </>
              )}
              {enabled &&
                !isFirstWeek &&
                goals.goals?.map((x) => (
                  <>
                    <GoalGrid
                      key={x.id}
                      goal={{ goal: x, progress: goals.progress[x.id] }}
                      goalCompletionStatus={goals.progress}
                      handleToggleActivityStatus={confirmGoalDialogController.openConfirmDialog}
                      enabled={enabled}
                    ></GoalGrid>
                  </>
                ))}
            </Grid>
          )}
        </CardContent>
        {enabled && (
          <CardActions
            sx={{
              alignItems: 'flex-end',
              justifyContent: 'flex-end',
              flexGrow: 1,
            }}
          >
            <Button variant="outlined" onClick={() => jailCardController.setJailCardOpen(true)}>
              Show Available Jail Cards
            </Button>
            <Button
              disabled={!enabled}
              variant={'contained'}
              onClick={() => exerciseHistoryController.setHistoryDialogOpen(true)}
            >
              View Recent Exercise
            </Button>
            {newGoalEnabled && (
              <Button
                disabled={!newGoalEnabled}
                variant={'contained'}
                onClick={updateGoalsDialogController.handleShowNewGoalDialog}
              >
                Update Goals
              </Button>
            )}
          </CardActions>
        )}
      </Card>
      <ExerciseHistory
        open={exerciseHistoryController.historyDialogOpen}
        onClose={() => exerciseHistoryController.setHistoryDialogOpen(false)}
        view={exerciseHistoryController.activityHistoryView}
      ></ExerciseHistory>
      <JailCardStatusDialog
        jailCards={jailCardController.jailCards}
        onClose={() => jailCardController.setJailCardOpen(false)}
        open={jailCardController.jailCardOpen}
      ></JailCardStatusDialog>
      <ConfirmGoalCompletionDialog
        open={confirmGoalDialogController.confirmGoalDialogOpen}
        onClose={confirmGoalDialogController.cancelConfirmDialog}
        selectedGoal={confirmGoalDialogController.selectedGoal!}
        addActivity={confirmGoalDialogController.addActivity}
        weekCompleted={confirmGoalDialogController.weekCompleted}
      ></ConfirmGoalCompletionDialog>
      {!updateGoalsDialogController.isActivityLookupLoading && updateGoalsDialogController.activityLookupData && (
        <UpdateGoalsDialog
          cohortUserId={cohortUserId}
          showNewGoalDialog={isGoalModalOpen}
          submitGoals={async (e, reason, goals) => {
            await Promise.all([
              await updateGoalsDialogController.submitGoals(e, reason, goals),
              await reloadSurveyDrawer(),
              reloadDashboardView?.(),
            ]);
          }}
          groupStartDate={ToDate(groupStartDate)}
          weekNumberIndexZero={weekNumberIndexZero}
          activities={updateGoalsDialogController.activityLookupData.activities}
          timeframes={updateGoalsDialogController.activityLookupData.timeframes}
        ></UpdateGoalsDialog>
      )}
    </>
  );
};

interface GoalGridProps {
  goal: GoalWithStatus;
  enabled: boolean;
  goalCompletionStatus: GoalCompletionStatus;
  handleToggleActivityStatus: (goalDetails: GoalWithStatus, idx: number) => void;
}

const GoalGrid: React.FC<GoalGridProps> = ({ goal, handleToggleActivityStatus, enabled, goalCompletionStatus }) => {
  const uiState = useUiState(enabled);
  const styles = {
    toggleButton: {
      border: 0,
      borderRadius: 30,
      padding: 0.25,
      margin: 0.5,
    } as SxProps,
  };
  return (
    <Grid key={goal.goal.id} sx={{ marginTop: '10px' }}>
      <Typography>{goal.goal.goalText}</Typography>

      {[...Array(goal.goal.frequency)].map((_, idx) =>
        // Only render toggle buttons for goals that have started
        {
          const hasCompleted = goalCompletionStatus[goal.goal.id]?.[idx] === GoalStatus.Succeeded;

          return ToDate(goal.goal.startDate) <= ToDate(DateTime.now()) ? (
            <ToggleButton
              disabled={!enabled}
              color={'primary'}
              onClick={() => {
                if (hasCompleted) return; // prevent action if already completed

                handleToggleActivityStatus(goal, idx);
              }}
              selected={goalCompletionStatus[goal.goal.id]?.includes(idx)}
              key={idx}
              sx={styles.toggleButton}
              value={`${goal.goal.id}-${idx}`}
              // The filled/outlined icons are the only signal for whether a rep is
              // done, and MUI drops its icons' own `data-testid` in production
              // builds — so on a deployed app that state is invisible to tests.
              // This marks it on our own markup, which survives the build.
              data-testid={hasCompleted ? 'goal-rep-complete' : 'goal-rep-incomplete'}
            >
              {hasCompleted ? <Circle color={uiState.buttonColor} /> : <CircleOutlined color={uiState.buttonColor} />}
            </ToggleButton>
          ) : null;
        }
      )}
    </Grid>
  );
};
